import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Send,
  Bot,
  User,
  Sparkles,
  Trash2,
  BookOpen,
  Calculator,
  Atom,
  FlaskConical,
  Code,
  Languages,
  Loader2,
  Image as ImageIcon,
  FileText,
  Copy,
  Check,
  Maximize2,
  Minimize2,
  MessageSquare,
  Lock,
  Zap,
  Crown,
} from 'lucide-react';
import { mahstryAITutor } from '../../services/mahstryAITutorService';
import { aiTutorSubscription } from '../../services/aiTutorSubscriptionService';
import AITutorSubscriptionModal from './AITutorSubscriptionModal';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';
import './MahStryAITutor.css';

const MahStryAITutor = ({ isOpen, onClose, initialSubject = 'General' }) => {
  const { currentUser } = useRealtime();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(initialSubject);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const subjects = [
    { name: 'General', icon: BookOpen, color: 'purple' },
    { name: 'Mathematics', icon: Calculator, color: 'blue' },
    { name: 'Physics', icon: Atom, color: 'indigo' },
    { name: 'Chemistry', icon: FlaskConical, color: 'green' },
    { name: 'Biology', icon: Languages, color: 'emerald' },
    { name: 'Computer Science', icon: Code, color: 'cyan' },
    { name: 'English', icon: BookOpen, color: 'pink' },
  ];

  // Initialize session when component opens
  useEffect(() => {
    if (isOpen && !sessionId) {
      checkAccessAndInitialize();
    }
  }, [isOpen, sessionId, currentUser]);

  // Check access and initialize
  const checkAccessAndInitialize = async () => {
    setIsCheckingAccess(true);
    
    if (!currentUser) {
      toast.error('Please login to use AI Tutor');
      onClose();
      return;
    }

    const accessCheck = await aiTutorSubscription.canUseAITutor(currentUser.uid);
    const imageCheck = await aiTutorSubscription.canUploadImage(currentUser.uid);
    
    // Always set usage info regardless of limit status
    setUsageInfo({
      ...accessCheck,
      imagesRemaining: imageCheck.remaining || 0,
      imagesTotal: imageCheck.total || 0,
    });
    
    if (!accessCheck.canUse && accessCheck.reason === 'limit_reached') {
      toast.error(
        `Daily limit reached! You've used all ${accessCheck.total} doubts today. ${
          accessCheck.plan.id === 'free' 
            ? 'Upgrade to get more doubts!' 
            : 'Come back tomorrow for more doubts.'
        }`
      );
      
      // Show upgrade modal for free users
      if (accessCheck.plan.id === 'free') {
        setShowSubscriptionModal(true);
      }
      setIsCheckingAccess(false);
      return;
    }

    setIsCheckingAccess(false);

    // Initialize session
    const newSessionId = `${currentUser.uid}_${Date.now()}`;
    setSessionId(newSessionId);
    
    // Add welcome message
    const planName = accessCheck.plan?.name || 'Free';
    const planId = accessCheck.plan?.id || 'free';
    const remaining = accessCheck.remaining || 0;
    const total = accessCheck.total || 1;
    const imagesRemaining = imageCheck.remaining || 0;
    const imagesTotal = imageCheck.total || 0;
    
    setMessages([
      {
        id: 'welcome',
        type: 'assistant',
        content: `Hi! I'm **MahStry AI Tutor** 🎓\n\nAsk me any academic question and I'll help you solve it. Need detailed explanation? Just ask!\n\n💡 **Your Plan:** ${planName}${planId === 'free' ? ' (Upgrade for more doubts!)' : ''}\n📊 **Remaining Today:** ${remaining}/${total} doubts\n📸 **Image Uploads:** ${imagesRemaining}/${imagesTotal} per day\n\n**Quick Tips:**\n- Be specific with your questions\n- Ask for "detailed explanation" if needed\n- Request "step-by-step solution" for problems${imagesTotal > 0 ? '\n- Upload images of problems for better help' : ''}\n\n⚠️ **Note:** I'm an AI assistant and may occasionally provide inaccurate information. Always verify important answers with your teachers or textbooks.`,
        timestamp: new Date(),
      },
    ]);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if ((!inputText.trim() && uploadedImages.length === 0) || isLoading) return;

    // Check cached usage info first
    if (!usageInfo || !usageInfo.plan || usageInfo.remaining === undefined) {
      toast.error('Unable to check doubt limit. Please try again.');
      return;
    }

    if (usageInfo.remaining <= 0) {
      // Show popup for doubt limit reached
      if (window.confirm(`❌ Daily Doubt Limit Reached!\n\nYou've used all ${usageInfo.total} doubts today.\n\n${
        usageInfo.plan.id === 'free' 
          ? 'Upgrade to get more doubts per day!' 
          : 'Your limit will reset tomorrow.'
      }\n\nWould you like to view subscription plans?`)) {
        setShowSubscriptionModal(true);
      }
      return;
    }

    // Convert images to base64 data URLs for OpenAI
    const imageDataUrls = await Promise.all(
      uploadedImages.map(async (img) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(img.file);
        });
      })
    );

    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: inputText || 'Please analyze this image',
      images: uploadedImages.map(img => img.previewUrl),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputText;
    setInputText('');
    const imagesToSend = [...imageDataUrls];
    setUploadedImages([]);
    setIsLoading(true);

    try {
      const response = await mahstryAITutor.sendMessage(sessionId, messageText || 'Please analyze this image', {
        subject: selectedSubject,
        userId: currentUser?.uid,
        images: imagesToSend,
      });

      if (response.success) {
        // Increment usage count in Firebase
        await aiTutorSubscription.incrementUsage(currentUser.uid);
        
        // Update cached usage info
        const newRemaining = usageInfo.remaining - 1;
        setUsageInfo((prev) => ({
          ...prev,
          remaining: newRemaining,
        }));

        const assistantMessage = {
          id: `assistant_${Date.now()}`,
          type: 'assistant',
          content: response.response,
          timestamp: response.timestamp,
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Show warning if running low
        if (newRemaining === 0) {
          // Show popup for last doubt used
          setTimeout(() => {
            if (window.confirm(`❌ Daily Doubt Limit Reached!\n\nYou've used all ${usageInfo.total} doubts today.\n\n${
              usageInfo.plan.id === 'free' 
                ? 'Upgrade to get more doubts per day!' 
                : 'Your limit will reset tomorrow.'
            }\n\nWould you like to view subscription plans?`)) {
              setShowSubscriptionModal(true);
            }
          }, 1000);
        } else if (newRemaining <= 3 && newRemaining > 0) {
          toast.warning(`Only ${newRemaining} doubts remaining today!`);
        }
      } else {
        toast.error(response.error || 'Failed to get response');
        const errorMessage = {
          id: `error_${Date.now()}`,
          type: 'error',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    if (window.confirm('Are you sure you want to clear the chat history?')) {
      mahstryAITutor.clearHistory(sessionId);
      setMessages([
        {
          id: 'welcome',
          type: 'assistant',
          content: `Chat cleared! I'm ready to help you with new questions. 😊`,
          timestamp: new Date(),
        },
      ]);
      setUploadedImages([]);
      toast.success('Chat history cleared');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check image upload limit from cached usage info
    if (!usageInfo || !usageInfo.plan || usageInfo.imagesRemaining === undefined) {
      toast.error('Unable to check image limit. Please try again.');
      return;
    }

    if (usageInfo.imagesRemaining === 0) {
      // Show popup for image limit reached
      if (window.confirm(`❌ Image Limit Reached!\n\nYou've used all ${usageInfo.imagesTotal} images today.\n\n${
        usageInfo.plan.id === 'free' 
          ? 'Upgrade to a paid plan to upload images!' 
          : 'Come back tomorrow for more image uploads.'
      }\n\nWould you like to view subscription plans?`)) {
        setShowSubscriptionModal(true);
      }
      return;
    }

    if (usageInfo.plan.imagesPerDay === 0) {
      // Show popup for plan limit
      if (window.confirm(`🔒 Image Upload Not Available\n\nYour ${usageInfo.plan.name} plan does not support image uploads.\n\nUpgrade to Basic, Pro, or Advanced plan to upload images!\n\nWould you like to view subscription plans?`)) {
        setShowSubscriptionModal(true);
      }
      return;
    }

    // Check if adding these images would exceed the limit
    if (uploadedImages.length + files.length > usageInfo.imagesRemaining) {
      toast.error(`You can only upload ${usageInfo.imagesRemaining} more image(s) today.`);
      return;
    }

    setIsUploadingImage(true);

    try {
      const newImages = [];
      
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Max size is 5MB`);
          continue;
        }

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        
        newImages.push({
          file,
          previewUrl,
          name: file.name,
          size: file.size,
        });

        // Increment usage count in Firebase
        await aiTutorSubscription.incrementImageUsage(currentUser.uid);
      }

      setUploadedImages((prev) => [...prev, ...newImages]);
      
      // Update cached usage info
      setUsageInfo((prev) => ({
        ...prev,
        imagesRemaining: Math.max(0, prev.imagesRemaining - newImages.length),
      }));

      toast.success(`${newImages.length} image(s) uploaded successfully`);

      // Show warning if running low
      const newRemaining = usageInfo.imagesRemaining - newImages.length;
      if (newRemaining === 0) {
        toast.error(`You've used all your image uploads for today!`);
      } else if (newRemaining <= 2 && newRemaining > 0) {
        toast.warning(`Only ${newRemaining} image upload(s) remaining today!`);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setIsUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => {
      const newImages = [...prev];
      // Revoke object URL to free memory
      URL.revokeObjectURL(newImages[index].previewUrl);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleCopyMessage = (content, index) => {
    navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const formatMessage = (content) => {
    // Format bold text
    let formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Format code blocks
    formatted = formatted.replace(/```(.*?)```/gs, '<pre class="bg-gray-100 dark:bg-gray-700 p-2 rounded my-2 overflow-x-auto"><code>$1</code></pre>');
    
    // Format inline code
    formatted = formatted.replace(/`(.*?)`/g, '<code class="bg-gray-100 dark:bg-gray-700 px-1 rounded">$1</code>');
    
    // Format line breaks
    formatted = formatted.replace(/\n/g, '<br/>');
    
    return formatted;
  };

  const handleSubscribe = async (planId) => {
    try {
      const result = await aiTutorSubscription.upgradeSubscription(
        currentUser.uid,
        planId,
        {
          method: 'razorpay',
          // Add payment details here
        }
      );

      if (result.success) {
        setShowSubscriptionModal(false);
        // Reinitialize with new plan
        await checkAccessAndInitialize();
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to process subscription');
    }
  };

  if (!isOpen) return null;

  // Show subscription modal if no access
  if (showSubscriptionModal) {
    return (
      <AITutorSubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          onClose();
        }}
        onSubscribe={handleSubscribe}
        currentPlan={usageInfo?.plan?.id || 'free'}
      />
    );
  }

  // Show loading while checking access
  if (isCheckingAccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-700 dark:text-gray-300">Checking access...</p>
        </div>
      </div>
    );
  }

  // Minimized view
  if (isMinimized) {
    return (
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="fixed bottom-4 right-4 z-50"
      >
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full p-4 shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 flex items-center space-x-2"
        >
          <Bot className="w-6 h-6" />
          <MessageSquare className="w-5 h-5" />
          {messages.length > 1 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {messages.length - 1}
            </span>
          )}
        </button>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed z-50 ${
          isFullscreen
            ? 'inset-0'
            : 'inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4'
        }`}
        onClick={isFullscreen ? undefined : onClose}
      >
        <motion.div
          initial={isFullscreen ? { opacity: 0 } : { scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={isFullscreen ? { opacity: 0 } : { scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className={`bg-white dark:bg-gray-800 shadow-2xl flex flex-col ${
            isFullscreen
              ? 'w-full h-full rounded-none'
              : 'rounded-lg w-full max-w-5xl h-[90vh]'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
                <Bot className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white flex items-center">
                  MahStry AI Tutor
                  <Sparkles className="w-4 h-4 ml-2 animate-pulse" />
                </h2>
                <p className="text-xs text-purple-100 flex items-center">
                  {usageInfo && usageInfo.plan && (
                    <>
                      <Zap className="w-3 h-3 mr-1" />
                      {usageInfo.remaining}/{usageInfo.total} doubts left today
                      {usageInfo.plan.id === 'free' && (
                        <button
                          onClick={() => setShowSubscriptionModal(true)}
                          className="ml-2 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-[10px] font-semibold transition-colors"
                        >
                          Upgrade
                        </button>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-gray-900 rounded-lg transition-all text-xs font-bold flex items-center shadow-md"
                title="View subscription plans"
              >
                <Crown className="w-3.5 h-3.5 mr-1" />
                {usageInfo && usageInfo.plan && usageInfo.plan.id === 'free' ? 'Upgrade' : 'Plans'}
              </button>
              <button
                onClick={handleClearChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              {isFullscreen ? (
                <button
                  onClick={() => setIsFullscreen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                  title="Exit fullscreen"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => setIsFullscreen(true)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                  title="Fullscreen"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Subject Selector */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center space-x-2 overflow-x-auto pb-2 scrollbar-hide">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                Subject:
              </span>
              <div className="flex items-center space-x-2 overflow-x-auto scrollbar-hide">
                {subjects.map((subject) => {
                  const Icon = subject.icon;
                  const isSelected = selectedSubject === subject.name;
                  return (
                    <button
                      key={subject.name}
                      onClick={() => setSelectedSubject(subject.name)}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                        isSelected
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 ring-2 ring-purple-500'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{subject.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50 dark:bg-gray-900">
            {messages.map((message, index) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`flex items-start space-x-2 max-w-[80%] ${
                    message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                      message.type === 'user'
                        ? 'bg-blue-600'
                        : message.type === 'error'
                        ? 'bg-red-600'
                        : 'bg-gradient-to-br from-purple-600 to-indigo-600'
                    }`}
                  >
                    {message.type === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>

                  {/* Message Content */}
                  <div
                    className={`rounded-lg p-2.5 max-w-full ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : message.type === 'error'
                        ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-md'
                    }`}
                  >
                    {/* Images if present */}
                    {message.images && message.images.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-2">
                        {message.images.map((imgUrl, idx) => (
                          <img
                            key={idx}
                            src={imgUrl}
                            alt={`Uploaded ${idx + 1}`}
                            className="max-w-[200px] max-h-[200px] rounded border border-gray-300 dark:border-gray-600"
                          />
                        ))}
                      </div>
                    )}
                    <div
                      className="text-sm leading-relaxed break-words"
                      dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                    />
                    <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-xs opacity-70">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      {message.type === 'assistant' && (
                        <button
                          onClick={() => handleCopyMessage(message.content, index)}
                          className="text-xs hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded transition-colors"
                          title="Copy message"
                        >
                          {copiedIndex === index ? (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-lg p-2.5 shadow-md">
                  <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Thinking...
                  </span>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {/* Uploaded Images Preview */}
            {uploadedImages.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {uploadedImages.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={img.previewUrl}
                      alt={img.name}
                      className="w-16 h-16 object-cover rounded border border-gray-300 dark:border-gray-600"
                    />
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex items-end space-x-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={
                  isUploadingImage || 
                  isLoading || 
                  !usageInfo || 
                  !usageInfo.plan ||
                  usageInfo.imagesRemaining === 0 || 
                  usageInfo.plan.imagesPerDay === 0
                }
                className={`p-2 rounded-lg transition-colors ${
                  usageInfo && usageInfo.plan && (usageInfo.imagesRemaining === 0 || usageInfo.plan.imagesPerDay === 0)
                    ? 'opacity-30 cursor-not-allowed bg-gray-200 dark:bg-gray-700'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50'
                }`}
                title={
                  !usageInfo || !usageInfo.plan
                    ? 'Loading...'
                    : usageInfo.plan.imagesPerDay === 0
                    ? 'Image upload not available in your plan'
                    : usageInfo.imagesRemaining === 0
                    ? 'Daily image limit reached'
                    : 'Upload images'
                }
              >
                <ImageIcon className={`w-5 h-5 ${
                  usageInfo && usageInfo.plan && (usageInfo.imagesRemaining === 0 || usageInfo.plan.imagesPerDay === 0)
                    ? 'text-gray-400'
                    : 'text-gray-500'
                }`} />
              </button>
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Ask your question... (Enter to send)"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none text-sm"
                  rows={2}
                  disabled={isLoading}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={
                  isLoading || 
                  (!inputText.trim() && uploadedImages.length === 0) ||
                  !usageInfo ||
                  !usageInfo.plan ||
                  usageInfo.remaining <= 0
                }
                className={`p-2.5 rounded-lg transition-all shadow-lg ${
                  usageInfo && usageInfo.plan && usageInfo.remaining <= 0
                    ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-50'
                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
                title={
                  !usageInfo || !usageInfo.plan
                    ? 'Loading...'
                    : usageInfo.remaining <= 0
                    ? 'Daily doubt limit reached'
                    : 'Send message'
                }
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              💡 Ask for "detailed explanation" or "step-by-step" if needed
              {usageInfo && usageInfo.imagesTotal > 0 && (
                <span className="ml-2">
                  📸 {usageInfo.imagesRemaining || 0}/{usageInfo.imagesTotal} images left today
                </span>
              )}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MahStryAITutor;
